"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

type ProgressProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: number; // 0-100 (or mapped by min/max)
  min?: number;
  max?: number;
  indicatorClassName?: string; // style the filled bar
  children?: React.ReactNode; // optional content inside the indicator
};

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v ?? 0));
}

/**
 * Progress (shadcn-style)
 * - Track with rounded background
 * - Indicator uses translateX, animated
 * - Accepts children to render inside the indicator (e.g., a label)
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      className,
      value = 0,
      min = 0,
      max = 100,
      indicatorClassName,
      children,
      ...props
    },
    ref
  ) => {
    const pct = clamp(((value - min) / (max - min)) * 100, 0, 100);

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={pct}
        className={cn(styleDrivers.progress.track, className)}
        {...props}
      >
        <div
          className={cn(styleDrivers.progress.indicator, indicatorClassName)}
          style={{ transform: `translateX(-${100 - pct}%)` }}
        >
          {children}
        </div>
      </div>
    );
  }
);
Progress.displayName = "Progress";

export { Progress };